import { httpApi } from '@api/http.api';
import { CONTENT_TYPE_FORM_DATA_HEADER, NO_REQUEST_TIMEOUT } from '@constants/api';
import type { TENANT_TYPES } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/tenant-info/constants.ts';
import { getPercentage } from '@utils/numbers.ts';

export interface DefaultUserPreferences {
    timeFormat: string;
    dateFormat: string;
}

export type TenantTypeKey = (typeof TENANT_TYPES)[keyof typeof TENANT_TYPES];

export interface TenantType {
    id: number;
    name: TenantTypeKey;
}

interface ClientTenant {
    alias: string;
    name: string;
    displayName: string;
    defaultUserPreferences: DefaultUserPreferences;
}

export interface CreateClientTenantRequest extends ClientTenant {
    tenantType: TenantTypeKey;
    tempFileId: string; // where tempFileId is the ID of the temp file uploaded before for new tenant
}

export interface CreateClientTenantResponse extends ClientTenant {
    id: number;
    url: string;
    tenantType: TenantType;
}

export interface TenantLogoResponse {
    id: number;
    url: string;
}

export interface ClientTenantDetails extends ClientTenant {
    id: number;
    url: string;
    logoUrl?: string; // TODO: determine if it should use url or logoUrl for tenant logo
    tenantType: TenantType;
}

export interface UpdateClientTenantRequest extends ClientTenant {
    fileId: string; // where fileId is the ID of the file uploaded before for exists user
    isActive: boolean;
    tenantType: TenantTypeKey;
}

export interface UpdateClientTenantResponse extends ClientTenant {
    id: number;
    url: string; //if we can return this here wil be nice, where url is the URL of the file uploaded before
    isActive: boolean;
    tenantType: TenantType;
}

export interface ClientTenantTypesResponse {
    host: string;
    tenantTypes: TenantType[];
}

const getTenantBaseUrl = (clientId: number) => `admin-center/clients/${clientId}/tenants`;

export const createApiClientTenantWithLogo = (
    clientId: number,
    payload: CreateClientTenantRequest,
): Promise<CreateClientTenantResponse> =>
    httpApi
        .post<CreateClientTenantResponse>(`${getTenantBaseUrl(clientId)}/with-image`, payload, {
            timeout: NO_REQUEST_TIMEOUT,
        })
        .then(({ data }) => data);

export const createOrUpdateApiClientTenantLogoWithUploadProgress = (
    clientId: number,
    formData: FormData,
    onUploadProgress?: (progress: number) => void,
    signal?: AbortSignal,
): Promise<TenantLogoResponse> =>
    httpApi
        .post<TenantLogoResponse>(`${getTenantBaseUrl(clientId)}/upload-temp-logo`, formData, {
            headers: CONTENT_TYPE_FORM_DATA_HEADER,
            signal,
            timeout: NO_REQUEST_TIMEOUT,
            onUploadProgress: (event) => {
                if (!event.total) return;
                onUploadProgress?.(getPercentage(event.loaded, event.total));
            },
        })
        .then(({ data }) => data);

export const getApiClientTenantByAlias = (clientId: number, alias: string): Promise<ClientTenantDetails> =>
    httpApi.get<ClientTenantDetails>(`${getTenantBaseUrl(clientId)}/${alias}`).then(({ data }) => data);

export const updateApiClientTenant = (
    clientId: number,
    alias: string,
    payload: UpdateClientTenantRequest,
): Promise<UpdateClientTenantResponse> =>
    httpApi.put<UpdateClientTenantResponse>(`${getTenantBaseUrl(clientId)}/${alias}`, payload).then(({ data }) => data);

export const deleteApiClientTenant = (clientId: number, alias: string, deleteTenantDatabase = false): Promise<any> =>
    httpApi
        .delete<any>(`${getTenantBaseUrl(clientId)}/${alias}`, { params: { deleteTenantDatabase } })
        .then(({ data }) => data);

export const getApiClientTenantTypes = (clientId: number): Promise<ClientTenantTypesResponse> =>
    httpApi.get<ClientTenantTypesResponse>(`${getTenantBaseUrl(clientId)}/tenant-types`).then(({ data }) => data);
